import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface PageSEOProps {
  title?: string;
  description?: string;
  keywords?: string[];
  image?: string;
  type?: 'website' | 'article' | 'product';
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  noindex?: boolean;
}

export const PageSEO: React.FC<PageSEOProps> = ({
  title,
  description,
  keywords = [],
  image,
  type = 'website',
  author = 'Paslawski Capital Management',
  publishedTime,
  modifiedTime,
  noindex = false
}) => {
  const location = useLocation();
  const baseUrl = 'https://pcmfinancial.ca';
  
  // Default values
  const defaultTitle = 'PCM Paslawski Capital Management';
  const defaultDescription = 'PCM Paslawski Capital Management provides comprehensive wealth management, insurance solutions, and financial planning services in Canada. Expert advisors helping you secure your financial future.';
  const defaultImage = `${baseUrl}/assets/pcm-logo.png`;
  
  // Construct full title
  const fullTitle = title ? `${title} | PCM Paslawski Capital Management` : defaultTitle;
  const metaDescription = description || defaultDescription;
  const metaImage = image ? `${baseUrl}${image}` : defaultImage;
  const canonicalUrl = `${baseUrl}${location.pathname}`;
  
  useEffect(() => {
    // Update document title
    document.title = fullTitle;
    
    // Update or create meta tags
    const updateMetaTag = (name: string, content: string, property?: boolean) => {
      const attrName = property ? 'property' : 'name';
      let element = document.querySelector(`meta[${attrName}="${name}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attrName, name);
        document.head.appendChild(element);
      }
      
      element.setAttribute('content', content);
    };
    
    // Basic meta tags
    updateMetaTag('description', metaDescription);
    if (keywords.length > 0) {
      updateMetaTag('keywords', keywords.join(', '));
    }
    updateMetaTag('author', author);
    
    // Robots meta tag
    if (noindex) {
      updateMetaTag('robots', 'noindex, nofollow');
    } else {
      updateMetaTag('robots', 'index, follow');
    }
    
    // Open Graph tags
    updateMetaTag('og:title', fullTitle, true);
    updateMetaTag('og:description', metaDescription, true);
    updateMetaTag('og:image', metaImage, true);
    updateMetaTag('og:url', canonicalUrl, true);
    updateMetaTag('og:type', type, true);
    updateMetaTag('og:site_name', 'PCM Paslawski Capital Management', true);
    
    // Twitter Card tags
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', fullTitle);
    updateMetaTag('twitter:description', metaDescription);
    updateMetaTag('twitter:image', metaImage);
    
    // Article-specific tags
    if (type === 'article') {
      if (publishedTime) {
        updateMetaTag('article:published_time', publishedTime, true);
      }
      if (modifiedTime) {
        updateMetaTag('article:modified_time', modifiedTime, true);
      }
      updateMetaTag('article:author', author, true);
    }
    
    // Update canonical link
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', canonicalUrl);
    
  }, [fullTitle, metaDescription, metaImage, canonicalUrl, keywords, author, type, publishedTime, modifiedTime, noindex]);
  
  return null;
};